import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui';
import { ConfirmModal } from '../components/ui/Modal';

function RsaListPage() {
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const [rsaList, setRsaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Controlli permessi
  const isAdmin = user?.ruolo === 100000000;

  useEffect(() => {
    const fetchRsa = async () => {
      try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);

        const result = await apiRequest(`/rsa?${params.toString()}`);
        setRsaList(result.value || []);
      } catch (error) {
        console.error('Error fetching RSA:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(fetchRsa, 300);
    return () => clearTimeout(debounce);
  }, [apiRequest, search]);

  const loadData = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const result = await apiRequest(`/rsa?${params.toString()}`);
      setRsaList(result.value || []);
    } catch (error) {
      console.error('Error fetching RSA:', error);
    }
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    try {
      await apiRequest(`/rsa/${itemToDelete.id}`, { method: 'DELETE' });
      showToast('RSA eliminata con successo', 'success');
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      loadData();
    } catch (error) {
      console.error('Error deleting RSA:', error);
      showToast(error.message || 'Errore durante l\'eliminazione', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">RSA</h1>
      </div>

      {/* Search */}
      <div className="card mb-6">
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Cerca RSA..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input flex-1"
          />
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {rsaList.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Comune</th>
                <th>Indirizzo</th>
                <th>Provincia</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rsaList.map((rsa) => (
                <tr key={rsa.id}>
                  <td className="font-medium">{rsa.nome || '-'}</td>
                  <td>{rsa.comune || '-'}</td>
                  <td>{rsa.indirizzo || '-'}</td>
                  <td>{rsa.provincia || '-'}</td>
                  <td>
                    <div className="flex gap-2">
                      <Link
                        to={`/rsa/${rsa.id}`}
                        className="text-primary-600 hover:underline"
                      >
                        Dettagli
                      </Link>
                      {isAdmin && (
                        <button
                          onClick={() => confirmDelete(rsa)}
                          className="text-red-600 hover:text-red-800"
                          title="Elimina"
                        >
                          Elimina
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500 text-center py-8">Nessuna RSA trovata</p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
        title="Elimina RSA"
        message={`Sei sicuro di voler eliminare la RSA "${itemToDelete?.nome}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}

export default RsaListPage;
